import { ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';

interface PublicMobileLayoutProps {
    title: string;
    onBack?: () => void;
    children: ReactNode;
    /**
     * Latimea coloanei pe ecran lat. `ingusta` (implicit) e coloana de citire si de formular;
     * `lata` e pentru ecrane cu doua coloane (lista colaboratorului). Pe telefon nu schimba nimic.
     */
    latime?: 'ingusta' | 'lata';
}

/**
 * Layout pentru pagini publice, dintr-o mana, fara sidebar si fara hamburger (UX §4.3).
 * Bara de 48px sus, safe-area pe toate laturile (R11), 100dvh nu 100vh (R2).
 *
 * Shell-ul e tot ce face — ce pune pagina sub bara, ierarhia informatiilor si indicatoarele
 * de progres sunt decizii vizuale separate, din consultanta UX (Task 14).
 */
export function PublicMobileLayout({ title, onBack, children, latime = 'ingusta' }: PublicMobileLayoutProps) {
    // Fara plafon, la 1440px continutul se intindea pe tot ecranul (randuri de ~200 de caractere).
    const coloana = latime === 'lata' ? 'mx-auto w-full max-w-5xl' : 'mx-auto w-full max-w-3xl';
    return (
        // De la `md` radacina e ea insasi scrollerul: `body` si `#root` au inaltime fixa si taie
        // tot ce depaseste (`main.css`), relaxat doar sub 768px. Fara `md:overflow-y-auto`, o lista
        // lunga de activitati ramanea taiata pe desktop, fara nicio bara de derulare (08.10).
        // Pe telefon nu se schimba nimic: acolo deruleaza documentul.
        <div className="flex min-h-[100dvh] flex-col bg-light text-dark md:h-dvh md:min-h-0 md:overflow-y-auto">
            <header
                className="safe-area-top sticky top-0 z-10 border-b border-hairline bg-light"
                style={{
                    paddingLeft: 'max(0.5rem, env(safe-area-inset-left))',
                    paddingRight: 'max(0.5rem, env(safe-area-inset-right))',
                }}
            >
              <div className={`flex min-h-12 items-center gap-2 ${coloana}`}>
                {onBack ? (
                    <button
                        type="button"
                        onClick={onBack}
                        aria-label="Înapoi"
                        className="touch-target flex items-center justify-center rounded-md"
                    >
                        <ArrowLeft className="size-5" />
                    </button>
                ) : (
                    <span className="size-11" aria-hidden />
                )}
                {/* Impachetare, nu trunchiere: `truncate` + `title` ascundea 66% din numele
                    scolii la 360 (S1, constatarea 27) in spatele unui tooltip la hover, care
                    nu exista pe atingere. Header-ul e `sticky`, deci creste pe verticala cand
                    titlul are nevoie de doua randuri -- nimic nu ramane ascuns. */}
                <h1 className="text-fluid-lg flex-1 break-words font-semibold">{title}</h1>
                <span className="size-11" aria-hidden />
              </div>
            </header>
            <main
                className="flex-1 px-4 py-4"
                style={{
                    paddingLeft: 'max(1rem, env(safe-area-inset-left))',
                    paddingRight: 'max(1rem, env(safe-area-inset-right))',
                    paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
                }}
            >
                <div className={coloana}>{children}</div>
            </main>
        </div>
    );
}
